import React from "react";

// Student and Senior Citizen plans. Eligibility is checked by uploading an ID
// that an admin reviews. Once approved, the user can buy the ANNUAL plan at
// the special price the admin set (the server charges that price, never this
// page). Monthly and Quarterly are always the standard price.

const OFFERS = [
  {
    category: "student",
    icon: "🎓",
    title: "Student",
    eligibility: "For school and college students.",
    documents: [
      "Valid school or educational institution ID card",
      "The card must be for the current academic year",
    ],
  },
  {
    category: "senior",
    icon: "🧓",
    title: "Senior Citizen",
    eligibility: "For readers aged 60 and above.",
    documents: [
      "Aadhaar Card or any valid government-issued ID",
      "Date of birth or age must be clearly visible on the document",
    ],
  },
];

// Special prices apply to the Annual plan only.
const SPECIAL_PLAN_ID = "annual";

const rupees = (paise) => Math.round(paise / 100);
const fmtPct = (pct) => (Number.isInteger(pct) ? String(pct) : Number(pct).toFixed(1));

/**
 * The special Annual price in paise from the API's `special.<category>`
 * (current servers send `amountPaise`; older ones only `discountPercent`).
 */
function specialPrice(entry, annualPaise) {
  const amount = Number(entry?.amountPaise);
  if (Number.isFinite(amount) && amount >= 100) return Math.min(amount, annualPaise);
  const pct = Number(entry?.discountPercent) || 0;
  return Math.max(100, Math.round((annualPaise * (100 - pct)) / 100));
}

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("en-IN", { dateStyle: "medium" });
}

function OfferCard({ offer, annualPlan, pricePaise, verification, signedIn, busyKey, onApply, onPay, onSignIn }) {
  const annualPaise = annualPlan?.amountPaise || 0;
  const pct = annualPaise > 0 ? Math.max(0, Math.round(((annualPaise - pricePaise) / annualPaise) * 1000) / 10) : 0;
  const status = verification?.status || "none";
  const canApply = status === "none" || status === "rejected" || status === "expired";

  return (
    <div className={`offer-card offer-${offer.category}`}>
      <div className="offer-badge">Savings available</div>

      <div className="offer-head">
        <span className="offer-icon" aria-hidden="true">{offer.icon}</span>
        <div>
          <h3>{offer.title}</h3>
          <p className="offer-eligibility">{offer.eligibility}</p>
        </div>
      </div>

      <div className="offer-discount">
        <span className="offer-pct">
          ₹{rupees(pricePaise)} <small>/ year</small>
        </span>
        <span className="offer-pct-sub">
          {pct > 0 ? `${fmtPct(pct)}% off the Annual plan (₹${rupees(annualPaise)})` : "On the Annual plan"}
        </span>
      </div>

      <div className="offer-docs">
        <div className="offer-docs-title">Documents required</div>
        <ul>
          {offer.documents.map((d) => (
            <li key={d}><span className="check">✓</span>{d}</li>
          ))}
        </ul>
      </div>

      {status === "rejected" && (
        <div className="offer-note offer-note--bad">
          <strong>Your last request was not approved.</strong>
          {verification.rejectReason ? ` ${verification.rejectReason}.` : ""} Please upload the
          document again.
        </div>
      )}
      {status === "expired" && (
        <div className="offer-note offer-note--bad">
          Your verification expired. Please upload a current document to continue.
        </div>
      )}
      {status === "pending" && (
        <div className="offer-note offer-note--wait">
          <strong>Under review.</strong> We have your document. Check back here once it is approved.
        </div>
      )}

      {status === "approved" ? (
        <div className="offer-pay">
          <div className="offer-note offer-note--ok">
            <strong>Verified.</strong>
            {verification.validUntil ? ` Valid until ${fmtDate(verification.validUntil)}.` : ""} Your
            special price is on the Annual plan:
          </div>
          {annualPlan && (
            <button
              type="button"
              className="offer-pay-btn"
              disabled={!!busyKey}
              onClick={() => onPay(annualPlan, offer.category)}
            >
              <span className="offer-pay-label">
                {annualPlan.label} <small>{annualPlan.period}</small>
              </span>
              <span className="offer-pay-price">
                {busyKey === `${offer.category}:${annualPlan.id}` ? (
                  <span className="spinner" />
                ) : (
                  <>
                    {pricePaise < annualPaise && <s>₹{rupees(annualPaise)}</s>}
                    <strong>₹{rupees(pricePaise)}</strong>
                  </>
                )}
              </span>
            </button>
          )}
        </div>
      ) : (
        <button
          type="button"
          className="offer-cta"
          disabled={status === "pending"}
          onClick={() => (signedIn ? onApply(offer.category) : onSignIn(offer.category))}
        >
          {status === "pending"
            ? "Under review"
            : canApply && status !== "none"
              ? "Upload document again"
              : "Verify and apply"}
        </button>
      )}
    </div>
  );
}

export default function SpecialOffers({
  plans,
  special,
  verifications,
  signedIn,
  busyKey,
  onApply,
  onPay,
  onSignIn,
}) {
  const annualPlan = plans.find((p) => p.id === SPECIAL_PLAN_ID);
  const annualPaise = annualPlan?.amountPaise || 0;

  return (
    <section className="offers-section">
      <div className="section-label">Special offers</div>
      <h2 className="offers-title">Student and Senior Citizen plans</h2>
      <p className="offers-sub">
        Upload your ID once. After our team approves it, you get the JB+ Annual plan at a special
        price.
      </p>
      <div className="offers-grid">
        {OFFERS.map((offer) => (
          <OfferCard
            key={offer.category}
            offer={offer}
            annualPlan={annualPlan}
            pricePaise={specialPrice(special?.[offer.category], annualPaise)}
            verification={verifications?.[offer.category]}
            signedIn={signedIn}
            busyKey={busyKey}
            onApply={onApply}
            onPay={onPay}
            onSignIn={onSignIn}
          />
        ))}
      </div>
    </section>
  );
}
